// Thin API client. Attaches the Cognito ID token as the Authorization bearer (the HTTP API
// JWT authorizer validates it), and handles the two Phase 0 flows: create-version-upload and
// poll-version-status.

import type { AppConfig } from './config';
import type { Session } from './auth';

export interface CreateVersionResponse {
  projectId: string;
  trackId: string;
  versionId: string;
  ordinal: number;
  assetId: string;
  key: string;
  uploadUrl: string;
  expiresIn: number;
  ingestStatus: string;
}

export interface AssetStatus {
  assetId: string;
  role: string;
  ingestStatus: 'pending' | 'analyzing' | 'ready' | 'failed';
  ingestError: string | null;
  originalFilename: string;
  sampleRateHz: number | null;
  bitDepth: number | null;
  channels: number | null;
  durationMs: number | null;
  integratedLufs: number | null;
  truePeakDbtp: number | null;
  checksumSha256: string | null;
  proxyKey: string | null;
  peaksKey: string | null;
}

export interface VersionStatus {
  versionId: string;
  projectId: string;
  trackId: string;
  assets: AssetStatus[];
}

async function req<T>(
  config: AppConfig,
  session: Session,
  method: string,
  path: string,
  body?: unknown
): Promise<T> {
  const res = await fetch(`${config.apiEndpoint}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${session.idToken}`,
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j.error) msg = j.error;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return (await res.json()) as T;
}

export function createVersion(
  config: AppConfig,
  session: Session,
  projectId: string,
  input: { filename: string; sizeBytes: number; trackName?: string; trackId?: string; notes?: string }
): Promise<CreateVersionResponse> {
  return req<CreateVersionResponse>(
    config,
    session,
    'POST',
    `/projects/${encodeURIComponent(projectId)}/versions`,
    input
  );
}

export function getVersion(
  config: AppConfig,
  session: Session,
  versionId: string
): Promise<VersionStatus> {
  return req<VersionStatus>(config, session, 'GET', `/versions/${encodeURIComponent(versionId)}`);
}

// --- Stems (Phase 4, task 23) ---

/** One stem to upload: the file plus an optional human label (defaults server-side to the name). */
export interface StemUpload {
  filename: string;
  sizeBytes: number;
  stemName?: string;
}

/** A minted presigned PUT for one stem in a batch. */
export interface StemUploadTarget {
  assetId: string;
  stemName: string;
  filename: string;
  key: string;
  uploadUrl: string;
}

export interface CreateStemVersionResponse {
  projectId: string;
  trackId: string;
  versionId: string;
  ordinal: number;
  expiresIn: number;
  assets: StemUploadTarget[];
}

export interface AddStemsResponse {
  projectId: string;
  trackId: string;
  versionId: string;
  expiresIn: number;
  assets: StemUploadTarget[];
}

/**
 * Create a stems version with an initial batch of stems, minting one presigned PUT per stem.
 * Omit trackId to create a new stems track (trackName required); pass trackId to add a new
 * stems version to an existing stems track. Upload each returned target with uploadToPresignedUrl.
 */
export function createStemVersion(
  config: AppConfig,
  session: Session,
  projectId: string,
  input: { stems: StemUpload[]; trackId?: string; trackName?: string; label?: string; notes?: string }
): Promise<CreateStemVersionResponse> {
  return req<CreateStemVersionResponse>(
    config,
    session,
    'POST',
    `/projects/${encodeURIComponent(projectId)}/stem-versions`,
    input
  );
}

/**
 * Append more stems (overdubs) to an EXISTING stems version, extending the set. Returns one
 * presigned PUT per added stem; upload each with uploadToPresignedUrl.
 */
export function addStemsToVersion(
  config: AppConfig,
  session: Session,
  versionId: string,
  stems: StemUpload[]
): Promise<AddStemsResponse> {
  return req<AddStemsResponse>(
    config,
    session,
    'POST',
    `/versions/${encodeURIComponent(versionId)}/stems`,
    { stems }
  );
}

/** Presigned GET for a stem's ORIGINAL (lossless) file — for downloading into a DAW. */
export function getStemDownloadUrl(
  config: AppConfig,
  session: Session,
  versionId: string,
  assetId: string
): Promise<{ projectId: string; versionId: string; assetId: string; url: string; filename: string; expiresIn: number }> {
  return req(
    config,
    session,
    'GET',
    `/versions/${encodeURIComponent(versionId)}/assets/${encodeURIComponent(assetId)}/download-url`
  );
}

/**
 * Delete a stem (its S3 objects + record). A manager may delete any stem; anyone else may delete
 * only stems they uploaded — the server enforces this and returns 403 otherwise.
 */
export function deleteStemAsset(
  config: AppConfig,
  session: Session,
  versionId: string,
  assetId: string
): Promise<{ versionId: string; assetId: string; deleted: boolean }> {
  return req(
    config,
    session,
    'DELETE',
    `/versions/${encodeURIComponent(versionId)}/assets/${encodeURIComponent(assetId)}`
  );
}

export interface PlaybackUrlResponse {
  projectId: string;
  versionId: string;
  assetId: string;
  url: string;
  expiresIn: number;
  contentType: string;
}

/** Get a short-lived presigned GET URL for an asset's streaming proxy (for playback). */
export function getPlaybackUrl(
  config: AppConfig,
  session: Session,
  versionId: string,
  assetId: string
): Promise<PlaybackUrlResponse> {
  return req<PlaybackUrlResponse>(
    config,
    session,
    'GET',
    `/versions/${encodeURIComponent(versionId)}/assets/${encodeURIComponent(assetId)}/playback-url`
  );
}

/** Precomputed waveform peaks envelope written by the ingest worker. `data` is int16-quantized
 *  interleaved min/max pairs; divide by 32768 to normalize to -1..1 for the waveform library. */
export interface PeaksResponse {
  projectId: string;
  versionId: string;
  assetId: string;
  durationMs: number | null;
  peaks: { version: number; buckets: number; bits: number; data: number[] };
}

/** Fetch an asset's precomputed waveform peaks (inline JSON) for rendering the waveform. */
export function getPeaks(
  config: AppConfig,
  session: Session,
  versionId: string,
  assetId: string
): Promise<PeaksResponse> {
  return req<PeaksResponse>(
    config,
    session,
    'GET',
    `/versions/${encodeURIComponent(versionId)}/assets/${encodeURIComponent(assetId)}/peaks`
  );
}

export interface Comment {
  commentId: string;
  versionId: string;
  tStartMs: number;
  tEndMs: number | null;
  body: string;
  mentions: string[];
  replyCount: number;
  author: string;
  authorEmail: string | null;
  resolved: boolean;
  pinnedVersionId: string | null;
  createdAt: string;
}

export interface Reply {
  replyId: string;
  commentId: string;
  versionId: string;
  body: string;
  mentions: string[];
  author: string;
  authorEmail: string | null;
  createdAt: string;
}

/** Create a timestamped comment (point when tEndMs omitted; region when tEndMs > tStartMs).
 *  pinnedVersionId pins the comment to a single version; omit/null = applies to all versions. */
export function createComment(
  config: AppConfig,
  session: Session,
  versionId: string,
  input: { body: string; tStartMs: number; tEndMs?: number | null; pinnedVersionId?: string | null }
): Promise<Comment> {
  return req<Comment>(
    config,
    session,
    'POST',
    `/versions/${encodeURIComponent(versionId)}/comments`,
    input
  );
}

/** List comments for a version, time-ordered. Optional state and/or author filters. */
export function listComments(
  config: AppConfig,
  session: Session,
  versionId: string,
  opts?: { state?: 'open' | 'resolved'; author?: string; pinnedScope?: 'this' }
): Promise<{ versionId: string; projectId: string; comments: Comment[] }> {
  const qs = new URLSearchParams();
  if (opts?.state) qs.set('state', opts.state);
  if (opts?.author) qs.set('author', opts.author);
  if (opts?.pinnedScope) qs.set('pinnedScope', opts.pinnedScope);
  const q = qs.toString() ? `?${qs.toString()}` : '';
  return req(config, session, 'GET', `/versions/${encodeURIComponent(versionId)}/comments${q}`);
}

/** Add a threaded reply to a comment. */
export function addReply(
  config: AppConfig,
  session: Session,
  versionId: string,
  commentId: string,
  body: string
): Promise<Reply> {
  return req<Reply>(
    config,
    session,
    'POST',
    `/versions/${encodeURIComponent(versionId)}/comments/${encodeURIComponent(commentId)}/replies`,
    { body }
  );
}

/** List replies for a comment, time-ordered. */
export function listReplies(
  config: AppConfig,
  session: Session,
  versionId: string,
  commentId: string
): Promise<{ commentId: string; replies: Reply[] }> {
  return req(
    config,
    session,
    'GET',
    `/versions/${encodeURIComponent(versionId)}/comments/${encodeURIComponent(commentId)}/replies`
  );
}

/** Resolve or reopen a comment. */
export function setCommentResolved(
  config: AppConfig,
  session: Session,
  versionId: string,
  commentId: string,
  resolved: boolean
): Promise<Comment> {
  return req<Comment>(
    config,
    session,
    'PATCH',
    `/versions/${encodeURIComponent(versionId)}/comments/${encodeURIComponent(commentId)}`,
    { resolved }
  );
}

export interface Project {
  projectId: string;
  name: string;
  type: string;
  loudnessRef: number;
  retentionDays: number;
  role: string;
  permissions: string[];
  createdAt: string | null;
}

/** Retention window bounds (mirror the API's projects.js constants). Change here + in the API
 *  together if the product default/ceiling ever moves. */
export const RETENTION_MIN_DAYS = 1;
export const RETENTION_MAX_DAYS = 120;
export const RETENTION_DEFAULT_DAYS = 60;

export type TrackKind = 'mix' | 'stems';

export interface ProjectTrack {
  trackId: string;
  name: string;
  kind: TrackKind;
  currentVersionId: string | null;
  approvedVersionId: string | null;
  versionCount: number;
  createdBy: string | null;
  createdAt: string | null;
}

/** Create a project. The caller becomes its manager (so it appears in their project list). */
export function createProject(
  config: AppConfig,
  session: Session,
  input: { name: string; type?: 'MIX_REVIEW' | 'MULTITRACK'; loudnessRef?: number; retentionDays?: number }
): Promise<Project & { role: string }> {
  return req(config, session, 'POST', '/projects', input);
}

/** List the projects the caller is a member of. */
export function listMyProjects(
  config: AppConfig,
  session: Session
): Promise<{ projects: Project[] }> {
  return req(config, session, 'GET', '/projects');
}

/**
 * Update a project's settings (currently retentionDays). Requires `manage`. The server clamps to
 * [RETENTION_MIN_DAYS, RETENTION_MAX_DAYS]. Retention is DYNAMIC: lowering it can make already-
 * uploaded versions eligible for deletion on the next daily sweep.
 */
export function updateProject(
  config: AppConfig,
  session: Session,
  projectId: string,
  input: { retentionDays: number }
): Promise<{ projectId: string; retentionDays: number }> {
  return req(config, session, 'PATCH', `/projects/${encodeURIComponent(projectId)}`, input);
}

// --- Admin console (admin-only; the API 403s a non-admin) ---

export interface AdminUser {
  sub: string | null;
  email: string | null;
  emailVerified: boolean;
  status: string | null;
  enabled: boolean;
  createdAt: string | null;
}

export interface AdminUserProject {
  projectId: string;
  name: string | null;
  type: string | null;
  role: string;
  permissions: string[];
  createdAt: string | null;
}

/** List every user in the pool (paginated). Admin-only. */
export function adminListUsers(
  config: AppConfig,
  session: Session,
  opts?: { paginationToken?: string; limit?: number }
): Promise<{ users: AdminUser[]; nextToken: string | null }> {
  const qs = new URLSearchParams();
  if (opts?.paginationToken) qs.set('paginationToken', opts.paginationToken);
  if (opts?.limit) qs.set('limit', String(opts.limit));
  const q = qs.toString() ? `?${qs.toString()}` : '';
  return req(config, session, 'GET', `/admin/users${q}`);
}

/** List one user's project memberships. Admin-only. */
export function adminGetUserProjects(
  config: AppConfig,
  session: Session,
  userId: string
): Promise<{ userId: string; projects: AdminUserProject[] }> {
  return req(config, session, 'GET', `/admin/users/${encodeURIComponent(userId)}/projects`);
}

export interface AdminProject {
  projectId: string;
  name: string | null;
  type: string | null;
  createdBy: string | null;
  createdByEmail: string | null;
  createdAt: string | null;
}

/** List EVERY project in the system (paginated). Admin-only. */
export function adminListProjects(
  config: AppConfig,
  session: Session,
  opts?: { paginationToken?: string }
): Promise<{ projects: AdminProject[]; nextToken: string | null }> {
  const qs = new URLSearchParams();
  if (opts?.paginationToken) qs.set('paginationToken', opts.paginationToken);
  const q = qs.toString() ? `?${qs.toString()}` : '';
  return req(config, session, 'GET', `/admin/projects${q}`);
}

/** Delete a project and ALL its data (tracks, versions, media, comments, members). Admin-only.
 *  IRREVERSIBLE. Returns a teardown summary. */
export function deleteProject(
  config: AppConfig,
  session: Session,
  projectId: string
): Promise<{ projectId: string; name: string | null; tracks: number; versions: number; deletedS3Objects: number; deletedDbItems: number }> {
  return req(config, session, 'DELETE', `/projects/${encodeURIComponent(projectId)}`);
}

/** List the tracks in a project (with per-track version counts). */
export function listProjectTracks(
  config: AppConfig,
  session: Session,
  projectId: string
): Promise<{ projectId: string; name: string; type: string; loudnessRef: number; retentionDays: number; tracks: ProjectTrack[] }> {
  return req(config, session, 'GET', `/projects/${encodeURIComponent(projectId)}/tracks`);
}

export interface VersionAssetMeta {
  assetId: string;
  role: string | null;
  ingestStatus: string;
  container: string | null;
  codec: string | null;
  sampleRateHz: number | null;
  bitDepth: number | null;
  channels: number | null;
  durationMs: number | null;
  integratedLufs: number | null;
  truePeakDbtp: number | null;
  checksumSha256: string | null;
  /** Declared upload size in bytes — the reference for the client-side size-vs-length proxy. */
  sizeBytes: number | null;
}

/** A non-blocking tech-check verdict on a stem (Phase 4, task 24). severity: info | warn | error. */
export interface TechCheck {
  key: 'sample_rate' | 'bit_depth' | 'clipping' | 'length' | string;
  severity: 'info' | 'warn' | 'error';
  expected: unknown;
  actual: unknown;
  message: string;
}

/** A version's asset with its stem label (stems versions) — the per-stem row in a stems version. */
export interface VersionAsset extends VersionAssetMeta {
  stemName: string | null;
  originalFilename: string | null;
  /** Who uploaded this stem (sub + email) — drives the "delete your own only" gate + a by-line. */
  uploadedBy: string | null;
  uploadedByEmail: string | null;
  /** Tech-check verdicts (stems only; empty for a clean stem, a mix asset, or one still ingesting). */
  techChecks: TechCheck[];
}

export interface VersionSummary {
  versionId: string;
  ordinal: number | null;
  label: string | null;
  notes: string | null;
  status: string | null;
  createdBy: string | null;
  createdByEmail: string | null;
  createdAt: string | null;
  restoredFromVersionId: string | null;
  kind: TrackKind;
  assetCount: number;
  primaryAsset: VersionAssetMeta | null;
  /** Full per-asset list. A mix version has one entry mirroring primaryAsset; a stems version
   *  has one entry per stem (each with its stemName + per-stem ingestStatus). */
  assets: VersionAsset[];
  /** Version-level rollup of stem tech-check verdicts (stems only; 0/0 for a mix). */
  techCheckSummary?: { errors: number; warns: number };
}

export interface TrackVersions {
  projectId: string;
  trackId: string;
  currentVersionId: string | null;
  approvedVersionId: string | null;
  versions: VersionSummary[];
}

export interface VersionDiff {
  projectId: string;
  a: { versionId: string; meta: VersionAssetMeta };
  b: { versionId: string; meta: VersionAssetMeta };
  fields: { field: string; a: unknown; b: unknown; changed: boolean }[];
  changedCount: number;
}

/** List all versions on a track (newest ordinal first) with their primary-asset metadata. */
export function listTrackVersions(
  config: AppConfig,
  session: Session,
  projectId: string,
  trackId: string
): Promise<TrackVersions> {
  return req(
    config,
    session,
    'GET',
    `/projects/${encodeURIComponent(projectId)}/tracks/${encodeURIComponent(trackId)}/versions`
  );
}

/** Restore/duplicate a prior version as a new latest version (reuses proxy/metadata, no re-ingest). */
export function restoreVersion(
  config: AppConfig,
  session: Session,
  versionId: string,
  input?: { label?: string; notes?: string }
): Promise<{ projectId: string; trackId: string; versionId: string; ordinal: number }> {
  return req(
    config,
    session,
    'POST',
    `/versions/${encodeURIComponent(versionId)}/restore`,
    input || {}
  );
}

/** Technical metadata diff between two versions. */
export function diffVersions(
  config: AppConfig,
  session: Session,
  versionId: string,
  againstVersionId: string
): Promise<VersionDiff> {
  return req(
    config,
    session,
    'GET',
    `/versions/${encodeURIComponent(versionId)}/diff?against=${encodeURIComponent(againstVersionId)}`
  );
}

export type MemberRole = 'viewer' | 'commenter' | 'approver' | 'contributor' | 'collaborator' | 'manager';

export interface Member {
  userId: string;
  email: string;
  role: string;
  permissions: string[];
  createdAt?: string;
}

export function inviteMember(
  config: AppConfig,
  session: Session,
  projectId: string,
  input: { email: string; role: MemberRole }
): Promise<Member & { newUser: boolean }> {
  return req(config, session, 'POST', `/projects/${encodeURIComponent(projectId)}/members`, input);
}

export function listMembers(
  config: AppConfig,
  session: Session,
  projectId: string
): Promise<{ projectId: string; members: Member[] }> {
  return req(config, session, 'GET', `/projects/${encodeURIComponent(projectId)}/members`);
}

/** One project activity-log entry. `actor` is a Cognito sub or a `system:*` id (e.g. the sweep);
 *  `detail` is an action-specific object (may be null). */
export interface ActivityEvent {
  eventId: string;
  actor: string;
  action: string;
  detail: Record<string, unknown> | null;
  createdAt: string;
}

/** List a project's activity log, newest first. Requires `view`. */
export function listActivity(
  config: AppConfig,
  session: Session,
  projectId: string,
  opts?: { limit?: number }
): Promise<{ projectId: string; events: ActivityEvent[] }> {
  const qs = opts?.limit ? `?limit=${opts.limit}` : '';
  return req(config, session, 'GET', `/projects/${encodeURIComponent(projectId)}/activity${qs}`);
}

export function setMemberRole(
  config: AppConfig,
  session: Session,
  projectId: string,
  userId: string,
  role: MemberRole
): Promise<Member> {
  return req(
    config,
    session,
    'PATCH',
    `/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(userId)}`,
    { role }
  );
}

export function removeMember(
  config: AppConfig,
  session: Session,
  projectId: string,
  userId: string
): Promise<{ userId: string; removed: boolean }> {
  return req(
    config,
    session,
    'DELETE',
    `/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(userId)}`
  );
}

export function createShareLink(
  config: AppConfig,
  session: Session,
  projectId: string,
  input: { access?: 'view' | 'comment'; expiresInDays?: number; password?: string }
): Promise<{ token: string; projectId: string; permissions: string[]; expiresAt: string }> {
  return req(
    config,
    session,
    'POST',
    `/projects/${encodeURIComponent(projectId)}/share-links`,
    input
  );
}

// --- Track-level permission overrides (replace semantics) -------------------
// An override fully REPLACES a member's project grant for one track — it can widen or narrow.
// Administering overrides requires the `manage` permission (the API 403s otherwise). Only an
// existing project member can carry an override.

/** A member's permission override on a single track. `role` is null when set via explicit perms. */
export interface TrackOverride {
  userId: string;
  email: string | null;
  role: MemberRole | null;
  permissions: string[];
  createdAt?: string;
}

/** List the track overrides on a track (which members have a non-default grant there). */
export function listTrackPermissions(
  config: AppConfig,
  session: Session,
  projectId: string,
  trackId: string
): Promise<{ projectId: string; trackId: string; overrides: TrackOverride[] }> {
  return req(
    config,
    session,
    'GET',
    `/projects/${encodeURIComponent(projectId)}/tracks/${encodeURIComponent(trackId)}/members`
  );
}

/**
 * Set (create or replace) a member's permissions on one track. Pass either a named `role` or an
 * explicit `permissions` array — the stored set IS the effective grant on that track.
 */
export function setTrackPermission(
  config: AppConfig,
  session: Session,
  projectId: string,
  trackId: string,
  userId: string,
  input: { role: MemberRole } | { permissions: string[] }
): Promise<{ projectId: string; trackId: string; userId: string; email: string | null; role: MemberRole | null; permissions: string[] }> {
  return req(
    config,
    session,
    'PUT',
    `/projects/${encodeURIComponent(projectId)}/tracks/${encodeURIComponent(trackId)}/members/${encodeURIComponent(userId)}`,
    input
  );
}

/** Remove a member's track override — they revert to their project-level grant on that track. */
export function removeTrackPermission(
  config: AppConfig,
  session: Session,
  projectId: string,
  trackId: string,
  userId: string
): Promise<{ projectId: string; trackId: string; userId: string; removed: boolean }> {
  return req(
    config,
    session,
    'DELETE',
    `/projects/${encodeURIComponent(projectId)}/tracks/${encodeURIComponent(trackId)}/members/${encodeURIComponent(userId)}`
  );
}

// --- Approvals (Phase 3) ---

export type ApprovalDecision = 'approved' | 'changes_requested' | 'revoked';

export interface ApprovalResult {
  versionId: string;
  decision: ApprovalDecision;
  status?: string;
  approvedVersionId: string | null;
  supersededVersionId: string | null;
  event: { eventId: string; createdAt: string };
}

export interface ApprovalEvent {
  eventId: string;
  versionId: string;
  actor: string;
  actorEmail: string | null;
  decision: ApprovalDecision;
  note: string | null;
  createdAt: string;
}

/**
 * Apply an approval decision to a version. `approved`/`revoked` require the `approve`
 * permission; `changes_requested` requires only `comment`.
 */
export function applyApproval(
  config: AppConfig,
  session: Session,
  versionId: string,
  decision: ApprovalDecision,
  note?: string
): Promise<ApprovalResult> {
  return req<ApprovalResult>(
    config,
    session,
    'POST',
    `/versions/${encodeURIComponent(versionId)}/approval`,
    { decision, note }
  );
}

/** List a track's approval history (newest first). */
export function listApprovals(
  config: AppConfig,
  session: Session,
  projectId: string,
  trackId: string
): Promise<{ trackId: string; events: ApprovalEvent[] }> {
  return req(
    config,
    session,
    'GET',
    `/projects/${encodeURIComponent(projectId)}/tracks/${encodeURIComponent(trackId)}/approvals`
  );
}

// --- Comment export (Phase 3) ---

export interface ExportFile {
  name: string;
  url: string;
  contentType: string;
}

export interface ExportResult {
  versionId: string;
  projectId: string;
  trackId: string;
  commentCount: number;
  exportedAt: string;
  expiresInSeconds: number;
  files: ExportFile[];
}

/**
 * Export a version's comments to CSV/JSON + Cubase & Pro Tools marker files, returning
 * short-lived presigned download URLs. Requires the `download` permission.
 */
export function exportComments(
  config: AppConfig,
  session: Session,
  versionId: string,
  opts?: { state?: 'open' | 'resolved' }
): Promise<ExportResult> {
  return req<ExportResult>(
    config,
    session,
    'POST',
    `/versions/${encodeURIComponent(versionId)}/export`,
    opts?.state ? { state: opts.state } : {}
  );
}

/** PUT the file bytes to the presigned URL (direct to S3; not through the API). */
export async function uploadToPresignedUrl(url: string, file: File): Promise<void> {
  const res = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!res.ok) throw new Error(`Upload failed: ${res.status} ${res.statusText}`);
}
